import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFontCache } from '../../hooks/useFontCache';
import {
  TOPO_BRASIL_TERMICA_PX,
  TOPO_CHASSI_TERMICA_COM_BRASIL_PX,
  TOPO_CHASSI_TERMICA_PX,
  normalizarAjusteAlturaTermica,
} from '../../lib/impressaoTermica';

/**
 * Componente para impressão térmica de chassi
 * 
 * IMPORTANTE: Este componente foi modificado para não usar declarações @font-face no CSS.
 * Em vez disso, as fontes são carregadas via API FontFace antes de renderizar.
 * Isso evita que arquivos TTF apareçam no painel Sources do navegador.
 * NÃO ADICIONAR declarações @font-face de volta ao código.
 */
const Matricial: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { loadFontFromCache } = useFontCache();
  const [fontLoaded, setFontLoaded] = useState(false);
  const [brasilFontLoaded, setBrasilFontLoaded] = useState(false);
  
  const chassi = searchParams.get('chassi') || '';
  const fonte = searchParams.get('fonte') || '';
  const tamanho = searchParams.get('tamanho') || '24';
  const espacamento = searchParams.get('espacamento') || '2';
  const incluirBrasil = searchParams.get('incluir_brasil') === 'true';
  // Ajuste de altura do cliente (px). Só vem na URL quando o cliente tem o ajuste ligado;
  // ausente ou inválido vale 0, que é a geometria padrão.
  const ajusteAltura = normalizarAjusteAlturaTermica(searchParams.get('ajuste_altura') ?? 0);
  // Padding negativo é inválido no CSS (a regra seria ignorada), por isso o piso em 0
  const topoChassi = Math.max(0, (incluirBrasil ? TOPO_CHASSI_TERMICA_COM_BRASIL_PX : TOPO_CHASSI_TERMICA_PX) + ajusteAltura);
  const topoBrasil = TOPO_BRASIL_TERMICA_PX + ajusteAltura;

  // Carregar as fontes antes de mostrar o conteúdo
  useEffect(() => {
    const carregarFontes = async () => {
      // Carrega a fonte principal
      if (fonte) {
        try {
          const success = await loadFontFromCache(fonte);
          if (success) {
            setFontLoaded(true);
          }
        } catch (error) {
          console.error('Erro ao carregar fonte principal:', error);
        }
      }

      // Carrega a fonte BRASIL apenas se for necessário exibi-la
      if (incluirBrasil) {
        try {
          const success = await loadFontFromCache('brasil.ttf');
          if (success) {
            setBrasilFontLoaded(true);
          }
        } catch (error) {
          console.error('Erro ao carregar fonte BRASIL:', error);
        }
      } else {
        // Se não precisar exibir BRASIL, marca como carregada para não bloquear a renderização
        setBrasilFontLoaded(true);
      }
    };

    carregarFontes();
  }, [fonte, loadFontFromCache, incluirBrasil]);

  useEffect(() => {
    // Só configura a impressão após ambas as fontes carregarem
    if (!fontLoaded || !brasilFontLoaded) return;

    const style = document.createElement('style');
    style.innerHTML = `
    body * {
        visibility: hidden;
      }
      #chassi-content, #chassi-content, #brasil, #brasil * {
        visibility: visible;
      }
      #chassi-content {
        position: fixed;
        top: 0;
        left: 0;
        padding-top: ${topoChassi}px;
        padding-right: 98px;        
        -webkit-font-smoothing: none;
        text-rendering: geometricPrecision;
        transform: scale(-1, 1);
      }
      #brasil {
        position: fixed;
        top: 0;
        left: 0;
        padding-top: ${topoBrasil}px;
        padding-left: 44px;
        -webkit-font-smoothing: none;
        text-rendering: geometricPrecision;
      }
      /* 
       * IMPORTANTE: As declarações @font-face foram removidas intencionalmente.
       * NÃO adicionar @font-face de volta neste CSS.
       * As fontes são carregadas via JavaScript usando a API FontFace antes da renderização.
       * Isso evita que os arquivos TTF apareçam no painel Sources do navegador.
       */
      @page {
        size: auto;
        margin: 0mm;
        size: landscape;
      }
      @media print {
        html, body {
          margin: 0;
          padding: 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        #chassi-content, #brasil {
          -webkit-font-smoothing: none;
          text-rendering: geometricPrecision;
        }
      }
    `;
    document.head.appendChild(style);

    const handleAfterPrint = () => {
      window.close();
    };

    window.addEventListener('afterprint', handleAfterPrint);
    
    // Pequeno delay para garantir que a fonte foi aplicada
    setTimeout(() => {
      window.print();
    }, 500);

    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
      document.head.removeChild(style);
    };
  }, [fontLoaded, brasilFontLoaded, fonte, topoChassi, topoBrasil]);

  if (!fontLoaded || !brasilFontLoaded) {
    return <div>Carregando fontes...</div>;
  }

  return (
    <div>
      <div 
        id="chassi-content"
        style={{ 
          fontFamily: `"${fonte.replace('.ttf', '')}"`,
          fontSize: `${tamanho}px`,
          letterSpacing: `${espacamento}px`,
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          textAlign: 'center',
          fontWeight: 'bold',
          WebkitFontSmoothing: 'none',
          textRendering: 'geometricPrecision',
        }}
      >
        {chassi}
        {incluirBrasil && (
          <p 
            id="brasil"
            style={{
              fontFamily: 'BRASIL',          
              fontSize: '20px',
              letterSpacing: '5px',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              textAlign: 'center',
              fontWeight: 'bold',
              WebkitFontSmoothing: 'none',
              textRendering: 'geometricPrecision',
            }}
          >
            BRASIL
          </p>
        )}
      </div>
    </div>
  );
};

export default Matricial;
